export type AppLocale = "en" | "ru" | "tonru"

/** `Intl`/calendar formatting: meme locale uses ru-RU like standard Russian. */
export function localeUsesRuRegionalFormat(locale: AppLocale): boolean {
  return locale === "ru" || locale === "tonru"
}

export const DEFAULT_APP_LOCALE: AppLocale = "en"

export const TRANSLATIONS = {
  en: {
    topBar: {
      onlineLabel: "Online: {count}",
      offlineLabel: "Offline",
      offlineBanner:
        "No internet connection detected. Data may be outdated. Swipe up to dismiss.",
    },
    bottomNav: {
      shop: "Shop",
      inventory: "Inventory", // перевести
      pvp: "PVP",
      soonBadge: "SOON",
      newBadge: "NEW",
      solo: "Solo",
      rewards: "Rewards",
      profile: "Profile",
    },
    inventory: {
      title: "Inventory", // перевести
      tabYourGifts: "Your gifts", // перевести
      tabShop: "Shop", // перевести
      tabMyAssets: "My assets", // перевести
      tabMarketplace: "Marketplace", // перевести
      emptyTitle: "No gifts yet", // перевести
      emptyHint:
        "Send a Telegram gift to the platform account to top up your inventory.", // перевести
      loadFailed: "Could not load your gifts.", // перевести
      retry: "Retry", // перевести
      empty: {
        body: "You have no gifts yet. Buy one or send to {handle}", // перевести
        send: "Send", // перевести
        goShopping: "Go shopping", // перевести
      },
    },
    marketplace: {
      buy: "Buy", // перевести
      buyFor: "Buy for {amount}", // перевести
      viewOnTelegram: "View on Telegram", // перевести
      buying: "Buying...", // перевести
      purchased: "Gift purchased", // перевести
      loadFailed: "Couldn't load the marketplace.", // перевести
      retry: "Retry", // перевести
      emptyTitle: "No gifts for sale", // перевести
      emptyHint: "Check back later — new gifts appear all the time.", // перевести
      errorPriceChanged: "Price has changed. Try again.", // перевести
      errorInsufficientBalance: "Not enough TON balance.", // перевести
      errorGiftGone: "This gift is no longer available.", // перевести
      errorPriceUnavailable: "Price is temporarily unavailable.", // перевести
      errorGeneric: "Couldn't complete the purchase.", // перевести
    },
    giftDetail: {
      withdraw: "Withdraw gift", // перевести
      playWithGift: "Play with this gift", // перевести
      attrModel: "Model", // перевести
      attrBackground: "Background", // перевести
      attrSymbol: "Symbol", // перевести
      confirmTitle: "Send to telegram account?", // перевести
      privacyWarning:
        "Check your Telegram privacy settings. If gift sending is blocked, Gamba cannot deliver gifts to you.", // перевести
      delayWarning: "Withdrawal can take a few minutes — please be patient.", // перевести
      rateHint: "Price based on current Stars/TON exchange rate.", // перевести
      insufficientFunds: "Insufficient TON balance", // перевести
      sendCta: "Send", // перевести
      depositCta: "Deposit", // перевести
      no: "No", // перевести
      yes: "Yes", // перевести
      unavailableInRound: "Gift is in an active round", // перевести
      unavailableHold:
        "Withdrawal and bets will be available from {date} (Telegram transfer hold)",
      unavailableHoldNoDate:
        "Withdrawal and bets are temporarily unavailable (Telegram transfer hold)",
      unavailableMetadata: "Withdrawal preparation in progress", // перевести
      unavailableGeneric: "Withdrawal is unavailable", // перевести
      relayerInsufficientStars:
        "Gift transfer is temporarily unavailable. Please contact support.",
      successTitle: "Gift withdrawal in progress", // перевести
      successBody:
        "We've started sending the gift to your Telegram account. You'll get a notification once it lands in your wallet.", // перевести
      successCta: "Great", // перевести
      withdrawalFailed: "Withdrawal failed", // перевести
    },
    common: {
      ton: "TON",
      trixBalance: "Gamba balance:",
      amount: "Amount",
      enterAmount: "Enter amount",
      connectWallet: "Connect wallet",
      disconnectWallet: "Disconnect wallet",
      loading: "Loading...",
      max: "Max",
      soon: "Soon",
      notAvailable: "Not available",
    },
    ui: {
      close: "Close",
    },
    deposit: {
      title: "Deposit",
      action: "Deposit {amount} TON",
      walletLimitWarning:
        "Not enough TON in wallet (max {amount} TON incl. network fee)",
      trixBalance: "Gamba balance:",
      toInventory: "To inventory", // перевести
      giftHint: "Buy a gift or send it to {handle}", // перевести
    },
    withdraw: {
      title: "Withdraw",
      action: "Withdraw {amount} TON",
      initiated: "Withdrawal initiated!",
      failed: "Withdrawal failed",
      failedInitiate: "Failed to initiate withdrawal",
      minWarning: "Minimum withdrawal: {amount} TON",
      dayLimitWarning: "Daily limit: {amount} TON",
      availableToWithdraw: "{available}/{cap} TON available to withdraw",
      goToMarketplace: "Go to marketplace", // перевести
    },
    betting: {
      solo: "Solo",
      squad: "Squad",
      depositTon: "Deposit TON",
      openBetSettings: "Open bet settings",
      setupBet: "Setup bet",
      addBetTon: "Add {amount} TON",
      ariaSoloBet: "Make a Solo bet",
      ariaSquadBet: "Make a Squad bet",
      ariaBetAmount: "Bet amount",
      sheetSetupTab: "Bet setup",
      sheetPresetsTab: "Bet presets",
      addStake: "Add",
      sheetYourBet: "Your bet",
      sheetSectorNow: "Sector now: {percent}%",
      sheetSectorAfter: "Sector after: {percent}%",
      assetTon: "Bet with TON", // перевести
      assetGift: "Bet with gifts", // перевести
      selectGifts: "Select gifts", // перевести
      depositGift: "Deposit gift", // перевести
      buyGift: "Buy gift", // перевести
      giftBetFailed: "Couldn't place gift bet", // перевести
      giftBetPartialFailed: "{failed} of {total} gift bets failed", // перевести
    },
    giftPicker: {
      yourGifts: "Your gifts", // перевести
      allIn: "All in", // перевести
      selectGiftsCta: "Select gifts", // перевести
      betCta: "Bet {amount}", // перевести
    },
    devMenu: {
      title: "Developer menu",
      description: "Local feature toggles and tools",
      close: "Close",
      applyOnRestart: "Changes apply after you reopen the app.",
      sectionFlags: "Feature flags",
      flagTournaments: "Tournaments",
      flagGifts: "Gifts",
      resetOverrides: "Reset to build defaults",
    },
    profile: {
      playerFallback: "Player",
      trixBalance: "Gamba Balance",
      withdraw: "Withdraw",
      deposit: "Deposit",
      activity: "Activity",
      depositItem: "Deposit",
      withdrawalItem: "Withdrawal",
      giftDepositItem: "Gift deposit", // перевести
      giftWithdrawalItem: "Gift withdrawal", // перевести
      giftWithdrawalFee: "Gift withdrawal fee", // перевести
      openLanguage: "Change language",
      languageSheetTitle: "Language",
      languageSheetDescription: "Choose app language",
      languageConfirm: "Confirm",
      localeEnglish: "English",
      localeRussian: "Russian",
      localeTonru: "Russian MTONGA",
    },
    walletPage: {
      title: "Wallet",
      subtitle: "Manage your funds",
      gameBalance: "Game Balance",
      quickTopUp: "Quick Top Up",
      customAmountHint: "Or enter custom amount",
      topUp: "Top Up",
      connectWalletFirst: "Please connect your wallet first",
      enterValidAmount: "Enter a valid amount",
    },
    pvpRoom: {
      waitingForBets: "Waiting for bets...",
      solo: "Solo",
      squad: "Squad",
      total: "Total",
      winner: "Winner",
      youReceive: "You receive", // перевести
      squadWins: "Squad wins",
      bettingEndsIn: "Betting ends in",
      deposit: "Deposit",
      playerFallback: "Player",
      bets: "Bets",
      joinsSquad: "Joins squad",
      winnerBang: "Winner!",
      placeYourBets: "Place your bets",
      bank: "Bank",
      waiting: "Waiting",
      waitingForPlayers: "Waiting for players",
      winChance: "Win chance: {percent}%",
      winChanceYou: "You: {percent}%",
      prizePool: "Prize pool",
      youSuffix: " (you)",
      cancelYourBets: "Cancel your bets",
      ariaGamesHistory: "Open games history",
      ariaCancelSquadBets: "Cancel your bets in this squad",
      clientCommitHashLabel: "Client commit",
      saltHashLabel: "Salt hash",
      copyClientCommitHashAria: "Copy client commit hash",
      copySaltHashAria: "Copy salt hash",
      bubbles: {
        waitingSolo: "SOLO",
        waitingSoloTricks: "SOLO + TRICKS",
        waitingSquadSolo: "SQUAD ⚔️ SOLO",
        waitingSquadSoloTricks: "SQUAD ⚔️ SOLO + TRICKS",
        opensSolo: "Opens game solo",
        opensSquad: "Opens squad",
        joinsSolo: "Joins solo",
        joinsSquad: "Joins squad",
        adds: "Adds",
        gameOn: "THE GAME IS ON!",
        tricksOn: "TRICKS ON. STEAL THE BANK! ✨",
        trickPush: "Twists the wheel! ⚡",
        trickFreeze: "Freezes it! 🥶",
        trickSmash: "Smashes sectors! ☠️",
        trickGeneric: "Used a trick",
        dontBlink: "DON'T BLINK… WHO IS THE 👑???",
      },
    },
    history: {
      title: "History",
      subtitle: "Your game rounds and results",
      noGames: "No games yet",
      noGamesHint: "Start playing to see your history here",
      na: "N/A",
      globalTitle: "Global History",
      globalComingSoon: "Global history coming soon",
      gamesHistoryTitle: "Games history",
      showMyGames: "Show my games",
      winner: "Winner",
      bet: "Bet",
      prize: "Prize",
      rate: "Rate",
      noRounds: "No rounds yet",
      today: "Today",
      yesterday: "Yesterday",
      unknown: "Unknown",
    },
    roundDetail: {
      roundDetails: "Round details",
      detailsDescription:
        "Detailed round info: winner, parties, bets, tricks and fairness data.",
      loadFailed: "Failed to load round details",
      retry: "Retry",
      winner: "Winner",
      solo: "Solo",
      chance: "chance",
      netWin: "Net win",
      parties: "Parties ({count})",
      bets: "Bets ({count})",
      tricks: "Tricks ({count})",
      fairness: "Fairness",
      hash: "Hash",
      legitCheckTitle: "Legit check",
      gameLabel: "Game",
      dateLabel: "Date",
      saltLabel: "Salt",
      checkAction: "Check",
      legitSuccess: "Game #{number} is Legit",
      copied: "Copied",
      copyFailed: "Could not copy",
      verifying: "Verifying…",
      verifyFailed: "Verification failed",
      openLegitInBrowser: "Verify in source",
      openLegitInBrowserHint:
        "Opens the public verifier in your system browser (recommended in Telegram).",
      legitCheckUrlTooLong:
        "Round data is too long for a link. Use Check here or open history on desktop.",
    },
    tricks: {
      chaos: "Chaos",
      freeze: "Freeze!",
      push: "Push!",
      usedYourTrick: "You used your trick",
      cantUseTricksInSquadMode: "Can't use tricks in squad mode",
    },
    toast: {
      depositInitiated: "Deposit initiated!",
      transactionCancelled: "Transaction cancelled",
      failedTonDeposit: "Failed to initiate deposit",
      failedCryptoBotInvoice: "Failed to create CryptoBot invoice",
      failedXRocketInvoice: "Failed to create xRocket invoice",
      betCancelled: "Bet cancelled",
      betsCancelled: "Bets cancelled",
      failedCancelBet: "Failed to cancel bet",
    },
    appLoader: {
      loadFailedTitle: "Could not load the game",
      loadFailedHint: "Check your internet connection or try again later.",
      reloadPage: "Reload page",
      connecting: "Connecting…",
      loadingRoom: "Loading room…",
      fetchingBalance: "Fetching balance…",
      ready: "Ready",
      wheelPixi: "Starting wheel…",
      shellAssets: "Loading assets…",
    },
    notFound: {
      message: "Oops! Page not found",
      returnHome: "Return to home",
    },
    rewards: {
      activityBonuses: "Activity bonuses",
      promocodePlaceholder: "Enter promocode",
      claim: "Claim",
      inviteFriends: "Invite friends",
      invited: "Invited",
      totalEarned: "Total earned",
      referralProgram: "Referral program",
      referralProgramHint:
        "Invite your friends and get 10% of the commission from their winnings.",
      referralLinkUnavailable: "Referral link unavailable",
      referralLinkCopied: "Referral link copied",
      claimError: "Something went wrong",
      promocodeError: {
        notFound: "Promocode not found",
        alreadyUsed: "You've already used this promocode",
        requirementNotMet:
          "You don't meet the requirements for this promocode yet",
        budgetExceeded: "This promocode is no longer available",
      },
      promocodeRewardName: {
        ton: "TON Reward",
        gift: "Gift",
      },
      promocodeWager: {
        tonTurnover: "Wager {amount} TON to unlock withdrawal",
        nftBet: "Place a bet with this gift to unlock it",
      },
      partnerTitle: "Have an audience? Become a Gamba partner",
      partnerDescription:
        "Up to 100% commission, custom bonuses for your audience, a dedicated manager, and your own tournaments.",
      becomeAffiliate: "Become an affiliate",
    },
    promocodeReward: {
      title: "You claimed",
      description: "Spin the wheel to wager and unlock withdrawals!",
      cta: "Play now",
    },
    tournamentWelcome: {
      upcomingTitle: "Tournament ahead!",
      startedTitle: "The first tournament has started!",
      description:
        "Lorem ipsum dolor sit amet consectetur. Lorem ipsum dolor sit amet consectetur.",
      untilStart: "Until start",
      untilEnd: "Until end",
      startsAt: "Starts in {date}",
      cta: "Let's play",
    },
    tournamentBanner: {
      prizePool: "Prize pool",
      endsIn: "Ends in",
      startsIn: "Starts in",
      ended: "Ended",
    },
    tournamentRewards: {
      sectionTitle: "Tournaments",
      endsIn: "ENDS IN",
      startsIn: "STARTS IN",
      ended: "ENDED",
      rules:
        "**Top 50** by profit + **30 PvP nominations** will win **TON & NFTs.** The pool grows every game. Ready to win?",
      livePrizePool: "Live prize pool",
      prizePool: "Prize pool",
      topPlayersPrefix: "Top",
      topPlayersSuffix: "players",
      viewLeaderboard: "View leaderboard",
      checkNominations: "Check nominations",
      startingSoon: "The tournament is starting soon.",
      startsAt: "Starts in {date}",
    },
    tournamentLeaderboard: {
      back: "Back",
      prizePool: "prize pool!",
      startsIn: "Starts in",
      endsIn: "Ends in",
      ended: "Ended",
      startsAt: "Starts in {date}",
      rules:
        "Top 50 by profit and 30 PvP nominations will win TON & NFTs. The prize pool grows with every game.", //перевести
      empty: "No participants yet.",
      showMore: "Show more",
      showLess: "Show less",
      rulesTitle: "Tournament Rules:",
      rule1:
        "How scoring works: Your rating is calculated based on the total volume of your wins in TON.", //перевести
      rule2:
        "Rewards distribution: At the end of each tournament, players receive rewards based on their position in the leaderboard.", //перевести
      rule3:
        "Rewards claiming: The displayed rewards will be given to the winners within a day of completion and will appear in the MRKT storage.", //перевести
      rule4:
        "Fair play only: Abuse, cheating, or other suspicious activity will result in a ban from the tournament without explanation.", //перевести
      rule5:
        "Match Manipulation & Farming: Participants who play streaks of 10 or more consecutive 1v1 matches against the same opponent between 00:00 and 08:00 UTC with a clear systematic imbalance in results will be disqualified without warning.", //перевести
      waiting: "Waiting",
    },
  },
  ru: {
    topBar: {
      onlineLabel: "Онлайн: {count}",
      offlineLabel: "Нет сети",
      offlineBanner:
        "Похоже, нет интернета. Данные могут быть неактуальны. Смахните вверх, чтобы скрыть.",
    },
    bottomNav: {
      shop: "Магазин",
      inventory: "Инвентарь",
      pvp: "PvP",
      soonBadge: "SOON",
      newBadge: "NEW",
      solo: "Соло",
      rewards: "Награды",
      profile: "Профиль",
    },
    inventory: {
      title: "Инвентарь",
      tabYourGifts: "Ваши гифты",
      tabShop: "Магазин",
      tabMyAssets: "Мои активы",
      tabMarketplace: "Маркетплейс",
      emptyTitle: "Пока нет гифтов",
      emptyHint:
        "Отправьте Telegram-гифт на аккаунт площадки, чтобы пополнить инвентарь.",
      loadFailed: "Не удалось загрузить гифты.",
      retry: "Повторить",
      empty: {
        body: "У вас пока нет гифтов. Купите или пришлите на {handle}",
        send: "Отправить",
        goShopping: "В магазин",
      },
    },
    marketplace: {
      buy: "Купить",
      buyFor: "Купить за {amount}",
      viewOnTelegram: "Открыть в Telegram",
      buying: "Покупаем...",
      purchased: "Гифт куплен",
      loadFailed: "Не удалось загрузить маркетплейс.",
      retry: "Повторить",
      emptyTitle: "Гифтов на продаже нет",
      emptyHint: "Загляните позже — новые гифты появляются постоянно.",
      errorPriceChanged: "Цена изменилась. Попробуйте ещё раз.",
      errorInsufficientBalance: "Недостаточно TON на балансе.",
      errorGiftGone: "Этот гифт больше недоступен.",
      errorPriceUnavailable: "Цена временно недоступна.",
      errorGeneric: "Не удалось купить гифт.",
    },
    giftDetail: {
      withdraw: "Вывести гифт",
      playWithGift: "Играть с этим гифтом",
      attrModel: "Модель",
      attrBackground: "Фон",
      attrSymbol: "Символ",
      confirmTitle: "Отправить на Telegram-аккаунт?",
      privacyWarning:
        "Проверьте настройки приватности Telegram. Если приём гифтов запрещён, Gamba не сможет доставить гифт.",
      delayWarning:
        "Вывод может занять несколько минут — пожалуйста, подождите.",
      rateHint: "Цена рассчитана по текущему курсу Stars/TON.",
      insufficientFunds: "Недостаточно средств",
      sendCta: "Отправить",
      depositCta: "Пополнить",
      no: "Нет",
      yes: "Да",
      unavailableInRound: "Гифт в активном раунде",
      unavailableHold:
        "Вывод и ставки будут доступны с {date} — ограничение Telegram на передачу гифта",
      unavailableHoldNoDate:
        "Вывод и ставки пока недоступны — ограничение Telegram на передачу гифта",
      unavailableMetadata: "Подготовка к выводу",
      unavailableGeneric: "Вывод сейчас недоступен",
      relayerInsufficientStars:
        "Передача гифта временно недоступна. Обратитесь в поддержку.",
      successTitle: "Вывод гифта запущен",
      successBody:
        "Мы инициировали отправку гифта на твой Telegram-аккаунт. Ты получишь уведомление, когда он поступит на кош.",
      successCta: "Отлично",
      withdrawalFailed: "Не удалось инициировать вывод",
    },
    common: {
      ton: "TON",
      trixBalance: "Баланс Gamba:",
      amount: "Сумма",
      enterAmount: "Введите сумму",
      connectWallet: "Подключить кошелек",
      disconnectWallet: "Отключить кошелек",
      loading: "Загрузка...",
      max: "Макс",
      soon: "Скоро",
      notAvailable: "Недоступно",
    },
    ui: {
      close: "Закрыть",
    },
    deposit: {
      title: "Пополнение",
      action: "Пополнить на {amount} TON",
      walletLimitWarning:
        "Недостаточно TON в кошельке (макс. {amount} TON с учётом комиссии сети)",
      trixBalance: "Баланс Gamba:",
      toInventory: "В инвентарь",
      giftHint: "Купите гифт или пришлите на {handle}",
    },
    withdraw: {
      title: "Вывод",
      action: "Вывести {amount} TON",
      initiated: "Вывод запущен!",
      failed: "Ошибка вывода",
      failedInitiate: "Не удалось инициировать вывод",
      minWarning: "Минимальный вывод: {amount} TON",
      dayLimitWarning: "Дневной лимит: {amount} TON",
      availableToWithdraw: "{available}/{cap} TON доступно к выводу",
      goToMarketplace: "В маркетплейс",
    },
    betting: {
      solo: "Соло",
      squad: "Сквад",
      depositTon: "Пополнить TON",
      openBetSettings: "Открыть настройки ставки",
      setupBet: "Настройка ставки",
      addBetTon: "Добавить {amount} TON",
      ariaSoloBet: "Сделать ставку в соло",
      ariaSquadBet: "Сделать ставку в сквад",
      ariaBetAmount: "Сумма ставки",
      sheetSetupTab: "Настройка ставки",
      sheetPresetsTab: "Быстрые суммы",
      addStake: "Добавить",
      sheetYourBet: "Ваша ставка",
      sheetSectorNow: "Сектор сейчас: {percent}%",
      sheetSectorAfter: "После ставки: {percent}%",
      assetTon: "Ставить в TON",
      assetGift: "Ставить гифтами",
      selectGifts: "Выбрать гифты",
      depositGift: "Закинуть гифт",
      buyGift: "Купить гифт",
      giftBetFailed: "Не удалось поставить гифт",
      giftBetPartialFailed: "Не удалось поставить {failed} из {total} гифтов",
    },
    giftPicker: {
      yourGifts: "Ваши гифты",
      allIn: "Все",
      selectGiftsCta: "Выбрать гифты",
      betCta: "Поставить {amount}",
    },
    devMenu: {
      title: "Меню разработчика",
      description: "Локальные переключатели и инструменты",
      close: "Закрыть",
      applyOnRestart:
        "Изменения применятся после повторного входа в приложение.",
      sectionFlags: "Фичефлаги",
      flagTournaments: "Турниры",
      flagGifts: "Гифты",
      resetOverrides: "Сбросить к настройкам сборки",
    },
    profile: {
      playerFallback: "Игрок",
      trixBalance: "Баланс Gamba",
      withdraw: "Вывести",
      deposit: "Пополнить",
      activity: "Активность",
      depositItem: "Пополнение",
      withdrawalItem: "Вывод",
      giftDepositItem: "Депозит гифта",
      giftWithdrawalItem: "Вывод гифта",
      giftWithdrawalFee: "Комиссия за вывод гифта",
      openLanguage: "Сменить язык",
      languageSheetTitle: "Язык",
      languageSheetDescription: "Выберите язык приложения",
      languageConfirm: "Подтвердить",
      localeEnglish: "English",
      localeRussian: "Russian",
      localeTonru: "Russian MTONGA",
    },
    walletPage: {
      title: "Кошелек",
      subtitle: "Управляйте средствами",
      gameBalance: "Игровой баланс",
      quickTopUp: "Быстрое пополнение",
      customAmountHint: "Или введите сумму",
      topUp: "Пополнить",
      connectWalletFirst: "Сначала подключите кошелек",
      enterValidAmount: "Введите корректную сумму",
    },
    pvpRoom: {
      waitingForBets: "Ожидание ставок...",
      solo: "Соло",
      squad: "Сквад",
      total: "Итого",
      winner: "Победитель",
      youReceive: "Вы получаете",
      squadWins: "Побеждает сквад",
      bettingEndsIn: "Ставки закроются через",
      deposit: "Пополнить",
      playerFallback: "Игрок",
      bets: "Ставит",
      joinsSquad: "Входит в сквад",
      winnerBang: "Победитель!",
      placeYourBets: "Сделайте ставку",
      bank: "Банк",
      waiting: "Ожидание",
      waitingForPlayers: "Ожидание игроков",
      winChance: "Шанс на победу: {percent}%",
      winChanceYou: "Вы: {percent}%",
      prizePool: "Призовой пул",
      youSuffix: " (вы)",
      cancelYourBets: "Отменить ставки",
      ariaGamesHistory: "Открыть историю игр",
      ariaCancelSquadBets: "Отменить ставки в этом скваде",
      clientCommitHashLabel: "Client commit",
      saltHashLabel: "Salt hash",
      copyClientCommitHashAria: "Скопировать client commit hash",
      copySaltHashAria: "Скопировать salt hash",
      bubbles: {
        waitingSolo: "СОЛО",
        waitingSoloTricks: "СОЛО + ТРИКИ",
        waitingSquadSolo: "СКВАД ⚔️ СОЛО",
        waitingSquadSoloTricks: "СКВАД ⚔️ СОЛО + ТРИКИ",
        opensSolo: "Открывает игру в соло",
        opensSquad: "Открывает сквад",
        joinsSolo: "В соло",
        joinsSquad: "В сквад",
        adds: "Добавляет",
        gameOn: "ИГРА ПОШЛА!",
        tricksOn: "ТРИКИ! ЗАБЕРИ БАНК! ✨",
        trickPush: "Пуш! ⚡",
        trickFreeze: "Замораживает! 🥶",
        trickSmash: "Ломает сектора! ☠️",
        trickGeneric: "Использовал трюк",
        dontBlink: "НЕ МОРГАЙ… КТО 👑???",
      },
    },
    history: {
      title: "История",
      subtitle: "Ваши игровые раунды и результаты",
      noGames: "Пока нет игр",
      noGamesHint: "Начните играть, чтобы увидеть историю здесь",
      na: "Н/Д",
      globalTitle: "Глобальная история",
      globalComingSoon: "Глобальная история скоро появится",
      gamesHistoryTitle: "История игр",
      showMyGames: "Показать мои игры",
      winner: "Победитель",
      bet: "Ставка",
      prize: "Приз",
      rate: "Коэф.",
      noRounds: "Пока нет раундов",
      today: "Сегодня",
      yesterday: "Вчера",
      unknown: "Неизвестно",
    },
    roundDetail: {
      roundDetails: "Детали раунда",
      detailsDescription:
        "Подробности раунда: победитель, участники, ставки, трики и данные честной игры.",
      loadFailed: "Не удалось загрузить детали раунда",
      retry: "Повторить",
      winner: "Победитель",
      solo: "Соло",
      chance: "шанс",
      netWin: "Чистый выигрыш",
      parties: "Партии ({count})",
      bets: "Ставки ({count})",
      tricks: "Трики ({count})",
      fairness: "Честность",
      hash: "Хэш",
      legitCheckTitle: "Проверка честности",
      gameLabel: "Игра",
      dateLabel: "Дата",
      saltLabel: "Соль",
      checkAction: "Проверить",
      legitSuccess: "Игра #{number} честная",
      copied: "Скопировано",
      copyFailed: "Не удалось скопировать",
      verifying: "Проверка…",
      verifyFailed: "Проверка не прошла",
      openLegitInBrowser: "Проверить в исходниках",
      openLegitInBrowserHint:
        "Откроется публичная страница проверки в системном браузере (удобно из Telegram).",
      legitCheckUrlTooLong:
        "Данные раунда слишком длинные для ссылки. Проверьте здесь кнопкой или с компьютера.",
    },
    tricks: {
      chaos: "Хаос",
      freeze: "Фриз!",
      push: "Пуш!",
      usedYourTrick: "Вы уже использовали трик",
      cantUseTricksInSquadMode: "В скваде трики недоступны",
    },
    toast: {
      depositInitiated: "Пополнение запущено!",
      transactionCancelled: "Транзакция отменена",
      failedTonDeposit: "Не удалось начать пополнение",
      failedCryptoBotInvoice: "Ошибка создания счёта CryptoBot",
      failedXRocketInvoice: "Ошибка создания счёта xRocket",
      betCancelled: "Ставка отменена",
      betsCancelled: "Ставки отменены",
      failedCancelBet: "Не удалось отменить ставку",
    },
    appLoader: {
      loadFailedTitle: "Не удалось загрузить игру",
      loadFailedHint: "Проверьте интернет или попробуйте позже.",
      reloadPage: "Перезагрузить страницу",
      connecting: "Подключение…",
      loadingRoom: "Загрузка комнаты…",
      fetchingBalance: "Проверка баланса…",
      ready: "Готово",
      wheelPixi: "Запуск рулетки…",
      shellAssets: "Загрузка оформления…",
    },
    notFound: {
      message: "Страница не найдена",
      returnHome: "На главную",
    },
    rewards: {
      activityBonuses: "Бонусы за активность",
      promocodePlaceholder: "Введите промокод",
      claim: "Активировать",
      inviteFriends: "Пригласить друзей",
      invited: "Приглашено",
      totalEarned: "Всего заработано",
      referralProgram: "Реферальная программа",
      referralProgramHint:
        "Приглашайте друзей и получайте 10% от комиссии с их выигрышей.",
      referralLinkUnavailable: "Реферальная ссылка недоступна",
      referralLinkCopied: "Ссылка скопирована",
      claimError: "Что-то пошло не так",
      promocodeError: {
        notFound: "Промокод не найден",
        alreadyUsed: "Вы уже активировали этот промокод",
        requirementNotMet: "Вы пока не выполнили условия этого промокода",
        budgetExceeded: "Этот промокод больше недоступен",
      },
      promocodeRewardName: {
        ton: "TON-награда",
        gift: "Подарок",
      },
      promocodeWager: {
        tonTurnover: "Отыграйте {amount} TON, чтобы открыть вывод",
        nftBet: "Сделайте ставку этим подарком, чтобы открыть его",
      },
      partnerTitle: "Есть аудитория? Станьте партнёром Gamba",
      partnerDescription:
        "До 100% комиссии, персональные бонусы для аудитории, личный менеджер и собственные турниры.",
      becomeAffiliate: "Стать партнёром",
    },
    promocodeReward: {
      title: "Вы получили бонус",
      description: "Крутите рулетку, чтобы отыграть его и открыть вывод!",
      cta: "Играть",
    },
    tournamentWelcome: {
      upcomingTitle: "Впереди турнир!",
      startedTitle: "Первый турнир начался!",
      description:
        "Lorem ipsum dolor sit amet consectetur. Lorem ipsum dolor sit amet consectetur.",
      untilStart: "До начала",
      untilEnd: "До конца",
      startsAt: "До старта {date}",
      cta: "Го играть",
    },
    tournamentBanner: {
      prizePool: "Призовой фонд",
      endsIn: "До конца",
      startsIn: "До старта",
      ended: "Завершён",
    },
    tournamentRewards: {
      sectionTitle: "Турниры",
      endsIn: "ДО КОНЦА",
      startsIn: "ДО СТАРТА",
      ended: "ЗАВЕРШЁН",
      rules:
        "**Топ-50** по прибыли + **30 PvP номинаций** выиграют **TON и NFT.** Пул растёт с каждой игрой. Готов выиграть?",
      livePrizePool: "Призовой фонд",
      prizePool: "Призовой фонд",
      topPlayersPrefix: "Топ",
      topPlayersSuffix: "игроков",
      viewLeaderboard: "Смотреть лидерборд",
      checkNominations: "Номинации",
      startingSoon: "Турнир скоро начнётся.",
      startsAt: "До старта {date}",
    },
    tournamentLeaderboard: {
      back: "Назад",
      prizePool: "призовой фонд!",
      startsIn: "До старта",
      endsIn: "До конца",
      ended: "Завершён",
      startsAt: "До старта {date}",
      rules:
        "Топ-50 по профиту и 30 PvP-номинаций получат TON и NFT. Призовой фонд растёт с каждой игрой.", //перевести
      empty: "Пока нет участников.",
      showMore: "Показать больше",
      showLess: "Свернуть",
      rulesTitle: "Правила турнира:",
      rule1:
        "Как считаются очки: Рейтинг рассчитывается из суммарного объёма ваших выигрышей в TON.", //перевести
      rule2:
        "Распределение наград: По окончании турнира игроки получают награды в зависимости от позиции в лидерборде.", //перевести
      rule3:
        "Получение наград: Указанные награды будут выданы победителям в течение суток после завершения и появятся в хранилище MRKT.", //перевести
      rule4:
        "Только честная игра: Абуз, читы и любая подозрительная активность приведут к бану в турнире без объяснения причин.", //перевести
      rule5:
        "Манипуляции и фарм: Участники, играющие серии из 10 и более подряд 1v1 матчей против одного и того же соперника с 00:00 до 08:00 UTC с явным систематическим перекосом результатов, будут дисквалифицированы без предупреждения.", //перевести
      waiting: "Ждём",
    },
  },
  tonru: {
    topBar: {
      onlineLabel: "Живые: {count}",
      offlineLabel: "Инет лёг",
      offlineBanner: "Инета нема. Инет принял ислам. Перезагрузи",
    },
    bottomNav: {
      shop: "Шмотки",
      inventory: "Лут", // перевести
      pvp: "Махач",
      soonBadge: "SOON",
      newBadge: "NEW",
      solo: "Соляново",
      rewards: "Халява",
      profile: "Профиль",
    },
    inventory: {
      title: "Лут", // перевести
      tabYourGifts: "Твои гифты", // перевести
      tabShop: "Шмотки", // перевести
      tabMyAssets: "Мои шмотки", // перевести
      tabMarketplace: "Базар", // перевести
      emptyTitle: "Гифтов нема", // перевести
      emptyHint: "Пришли гифт на акк площадки — подвалят сюда.", // перевести
      loadFailed: "Гифты не подсосались.", // перевести
      retry: "Еще раз", // перевести
      empty: {
        body: "Гифтов пока нема. Купи или зашли на {handle}", // перевести
        send: "Заслать", // перевести
        goShopping: "За шмотками", // перевести
      },
    },
    marketplace: {
      buy: "Купить", // перевести
      buyFor: "Брать за {amount}", // перевести
      viewOnTelegram: "Глянуть в Telegram", // перевести
      buying: "Берём...", // перевести
      purchased: "Гифт твой", // перевести
      loadFailed: "Базар не открылся.", // перевести
      retry: "Ещё раз", // перевести
      emptyTitle: "Пусто на базаре", // перевести
      emptyHint: "Загляни попозже, шмотки подвезут.", // перевести
      errorPriceChanged: "Прайс поменялся. Жми ещё.", // перевести
      errorInsufficientBalance: "TON-ов не хватает.", // перевести
      errorGiftGone: "Эту шмотку увели.", // перевести
      errorPriceUnavailable: "Прайс пока недоступен.", // перевести
      errorGeneric: "Купить не получилось.", // перевести
    },
    giftDetail: {
      withdraw: "Лутать гифт", // перевести
      playWithGift: "Закинуть в игру", // перевести
      attrModel: "Модел", // перевести
      attrBackground: "Фон", // перевести
      attrSymbol: "Символ", // перевести
      confirmTitle: "Слать на ТГ-акк?", // перевести
      privacyWarning:
        "Чекни приваты в ТГ. Если приём гифтов на замке, Gamba не докинет.", // перевести
      delayWarning: "Вывод может подзависнуть — потерпи.", // перевести
      rateHint: "Цена по текущему курсу Stars/TON.", // перевести
      insufficientFunds: "Бабла не хватает", // перевести
      sendCta: "Шли", // перевести
      depositCta: "Закинуть", // перевести
      no: "Не", // перевести
      yes: "Го", // перевести
      unavailableInRound: "Гифт сейчас в катке", // перевести
      unavailableHold:
        "Вывод и ставки откроются с {date} — Telegram держит гифт",
      unavailableHoldNoDate:
        "Вывод и ставки на стопе — Telegram держит гифт",
      unavailableMetadata: "Подкачка к выводу", // перевести
      unavailableGeneric: "Вывод сейчас занят", // перевести
      relayerInsufficientStars:
        "Передача гифта временно недоступна. Обратитесь в поддержку.",
      successTitle: "Гифт уже летит", // перевести
      successBody:
        "Кинули гифт на твой ТГ-акк. Прилетит уведомление, когда приземлится в кошель.", // перевести
      successCta: "Спс", // перевести
      withdrawalFailed: "Кэшаут зафейлился", // перевести
    },
    common: {
      ton: "TON",
      trixBalance: "В заначке Gamba:",
      amount: "Скока?",
      enterAmount: "Вбей цифру",
      connectWallet: "Врубай кошель",
      disconnectWallet: "Отрубай кошель",
      loading: "Пыхтим...",
      max: "Фулл",
      soon: "КТТС",
      notAvailable: "Обломчик",
    },
    ui: {
      close: "Оффнуть",
    },
    deposit: {
      title: "Додеп",
      action: "Додепать {amount} TON",
      walletLimitWarning:
        "В кошельке не хватает TON (макс. {amount} TON с комсой сети)",
      trixBalance: "В заначке Gamba:",
      toInventory: "В шмотки", // перевести
      giftHint: "Купи гифт или закинь на {handle}", // перевести
    },
    withdraw: {
      title: "Кэшаут",
      action: "Лутать {amount} TON",
      initiated: "Бабки пошли!",
      failed: "Не фартануло",
      failedInitiate: 'Кошель сказал "нет"',
      minWarning: "Минимум на выход: {amount} TON",
      dayLimitWarning: "Лимит на день: {amount} TON",
      availableToWithdraw: "Можно вывести: {available}/{cap} TON",
      goToMarketplace: "На маркет", // перевести
    },
    betting: {
      solo: "Соло",
      squad: "Толпа",
      depositTon: "Додепать TON",
      openBetSettings: "Подкрутить ставку",
      setupBet: "Че ставим?",
      addBetTon: "Вкинуть {amount} TON",
      ariaSoloBet: "Вкинуть в соляш",
      ariaSquadBet: "Вкинуть в толпу",
      ariaBetAmount: "Скока вкинуть",
      sheetSetupTab: "Че ставим?",
      sheetPresetsTab: "Фаст-вкид",
      addStake: "Вкинуть",
      sheetYourBet: "Твой вкид",
      sheetSectorNow: "Сектор щас {percent}%",
      sheetSectorAfter: "После вкида {percent}%",
      assetTon: "Кидать TON", // перевести
      assetGift: "Кидать гифтами", // перевести
      selectGifts: "Тыкай гифты", // перевести
      depositGift: "Закинуть гифт", // перевести
      buyGift: "Купить гифт", // перевести
      giftBetFailed: "Не зашел гифт во вкид", // перевести
      giftBetPartialFailed: "{failed} из {total} гифтов не зашли", // перевести
    },
    giftPicker: {
      yourGifts: "Твои гифты", // перевести
      allIn: "Все", // перевести
      selectGiftsCta: "Тыкай гифты", // перевести
      betCta: "Кинуть {amount}", // перевести
    },
    devMenu: {
      title: "Дев-меню",
      description: "Локальные тумблеры и тулзы",
      close: "Закрыть",
      applyOnRestart: "Вкатится после повторного захода в аппку.",
      sectionFlags: "Фичефлаги",
      flagTournaments: "Турниры",
      flagGifts: "Гифты",
      resetOverrides: "Как в сборке",
    },
    profile: {
      playerFallback: "Луд",
      trixBalance: "Баланс Gamba",
      withdraw: "Кэшаут",
      deposit: "Додеп",
      activity: "Движуха",
      depositItem: "Додеп",
      withdrawalItem: "Кэшаут",
      giftDepositItem: "Гифт залетел", // перевести
      giftWithdrawalItem: "Гифт вылетел", // перевести
      giftWithdrawalFee: "Комса за вывод гифта", // перевести
      openLanguage: "Сменить язык",
      languageSheetTitle: "Язык",
      languageSheetDescription: "Выбирай локаль",
      languageConfirm: "Применить",
      localeEnglish: "English",
      localeRussian: "Russian",
      localeTonru: "Russian MTONGA",
    },
    walletPage: {
      title: "Лопатник",
      subtitle: "Твои шекели тут",
      gameBalance: "Бабки в игре",
      quickTopUp: "Фаст додеп",
      customAmountHint: "Или вбей ручками",
      topUp: "Закинуть",
      connectWalletFirst: "Кошель привяжи для начала",
      enterValidAmount: "Нормальные цифры введи",
    },
    pvpRoom: {
      waitingForBets: "Ждем котлеты...",
      solo: "Соло",
      squad: "Толпа",
      total: "Итого",
      winner: "Батя",
      youReceive: "Тебе капает", // перевести
      squadWins: "Толпа тащит",
      bettingEndsIn: "Лавочка закроется через",
      deposit: "Додеп",
      playerFallback: "Чел",
      bets: "Льет",
      joinsSquad: "Влетел в толпу",
      winnerBang: "Батя в здании!",
      placeYourBets: "Кидайте бабки",
      bank: "Общак",
      waiting: "Ждем...",
      waitingForPlayers: "Где все? Ждем",
      winChance: "Шанс лутануть: {percent}%",
      winChanceYou: "Ты: {percent}%",
      prizePool: "Жирный куш",
      youSuffix: " (ты)",
      cancelYourBets: "Зассал? Бэкай",
      ariaGamesHistory: "Чекнуть логи",
      ariaCancelSquadBets: "Бэкнуть вкиды из толпы",
      clientCommitHashLabel: "Client commit",
      saltHashLabel: "Salt hash",
      copyClientCommitHashAria: "Скопировать client commit",
      copySaltHashAria: "Скопировать salt hash",
      bubbles: {
        waitingSolo: "СОЛЯШ",
        waitingSoloTricks: "СОЛЯШ + ТРИКИ",
        waitingSquadSolo: "ТОЛПА ⚔️ СОЛО",
        waitingSquadSoloTricks: "ТОЛПА ⚔️ СОЛО + ТРИКИ",
        opensSolo: "Стартует один",
        opensSquad: "Собирает толпу",
        joinsSolo: "Влетает сам",
        joinsSquad: "Влетел в толпу",
        adds: "Докинул",
        gameOn: "ПОГНАЛИ ЛУДИТЬ!",
        tricksOn: "ТРИКИ ТУТ. ЛУТАЙ ОБЩАК! ✨",
        trickPush: "Дал газу! ⚡",
        trickFreeze: "Дал по тормозам! 🥶",
        trickSmash: "Разнес кабины! ☠️",
        trickGeneric: "Нажал кнопку",
        dontBlink: "НЕ МОРГАЙ… КТО ТУТ 👑???",
      },
    },
    history: {
      title: "Логи",
      subtitle: "Где поднял, а где слил",
      noGames: "Глухо как в танке",
      noGamesHint: "Иди вкинь че-нить, тут пусто",
      na: "ХЗ",
      globalTitle: "Общие логи",
      globalComingSoon: "Общие логи еще пилятся",
      gamesHistoryTitle: "Твои катки",
      showMyGames: "Только мои катки",
      winner: "Батя",
      bet: "Вкид",
      prize: "Куш",
      rate: "Иксы",
      noRounds: "Каток не было",
      today: "Сёня",
      yesterday: "Вчерась",
      unknown: "Ноунейм",
    },
    roundDetail: {
      roundDetails: "Че по катке?",
      detailsDescription: "Кто кого нагнул, сколько вкинули и вот это всё.",
      loadFailed: "Не смогли подсосать дату",
      retry: "Еще раз",
      winner: "Батя",
      solo: "Соло",
      chance: "шанс",
      netWin: "Чистыми",
      parties: "Пацаны ({count})",
      bets: "Вкиды ({count})",
      tricks: "Трики ({count})",
      fairness: "Без скама",
      hash: "Хэш",
      legitCheckTitle: "Чек на чистоту",
      gameLabel: "Игра",
      dateLabel: "Когда",
      saltLabel: "Соль",
      checkAction: "Чекнуть",
      legitSuccess: "Игра #{number} без подкрута",
      copied: "В буфере",
      copyFailed: "Копир не вышел",
      verifying: "Чекаем…",
      verifyFailed: "Чек провалился",
      openLegitInBrowser: "Чекнуть по сорцам",
      openLegitInBrowserHint: "Вылезет браузер со страницей проверки.",
      legitCheckUrlTooLong:
        "Ссылка не влезает — дата жирная. Чекни тут кнопкой или с десктопа.",
    },
    tricks: {
      chaos: "Дичь",
      freeze: "Фриз!",
      push: "Пуш!",
      usedYourTrick: "Свой трик ты уже просрал",
      cantUseTricksInSquadMode: "В толпе трики оффнуты",
    },
    toast: {
      depositInitiated: "Додеп поехал!",
      transactionCancelled: "Транза померла",
      failedTonDeposit: "Додеп забуксовал",
      failedCryptoBotInvoice: "CryptoBot счёт не завёлся",
      failedXRocketInvoice: "xRocket счёт не завёлся",
      betCancelled: "Вкид бэкнут",
      betsCancelled: "Вкиды бэкнуты",
      failedCancelBet: "Вкид прилип, не отменить",
    },
    appLoader: {
      loadFailedTitle: "Не взлетела игра",
      loadFailedHint: "Инет спит или бэк в ауте. Перетыкни попозже.",
      reloadPage: "Рефрешни страницу",
      connecting: "Коннектимся…",
      loadingRoom: "Комнату подтягиваем…",
      fetchingBalance: "Щупаем баланс…",
      ready: "Погнали",
      wheelPixi: "Рулетка заводится…",
      shellAssets: "Скины грузим…",
    },
    notFound: {
      message: "Тут глухо, бро",
      returnHome: "На базу",
    },
    rewards: {
      activityBonuses: "Бонусы за движ",
      promocodePlaceholder: "Промик сюды",
      claim: "Забрать",
      inviteFriends: "Зазывай дружков",
      invited: "Уже позвали",
      totalEarned: "Ван лав суммарно",
      referralProgram: "Рефка",
      referralProgramHint:
        "Кинь друзьям инвайт — получай 10% с их комиссии по выигрышам.",
      referralLinkUnavailable: "Рефка не открывается",
      referralLinkCopied: "Скопировано в буф",
      claimError: "Жеско не вышло",
      promocodeError: {
        notFound: "Промик не найден",
        alreadyUsed: "Ты уже хапнул этот промик",
        requirementNotMet: "Условия пока не вытянул",
        budgetExceeded: "Промик уже не тянет, всё разобрали",
      },
      promocodeRewardName: {
        ton: "TON-плюхи",
        gift: "Подарос",
      },
      promocodeWager: {
        tonTurnover: "Откатай {amount} TON — и забирай вывод",
        nftBet: "Закинь этим подаросом ставку, чтобы открыть его",
      },
      partnerTitle: "Есть паблос? Полетай в партнёры Gamba",
      partnerDescription:
        "До 100% комсы, свои бонусы для аудитории, менеджер и турники.",
      becomeAffiliate: "В партнёры",
    },
    promocodeReward: {
      title: "Залутано!",
      description: "Крути рулу, отбивай вагер и разлочь кэшаут!",
      cta: "Го лудить",
    },
    tournamentWelcome: {
      upcomingTitle: "Скоро турик!",
      startedTitle: "Первый турик пошёл!",
      description:
        "Lorem ipsum dolor sit amet consectetur. Lorem ipsum dolor sit amet consectetur.",
      untilStart: "До старта",
      untilEnd: "До финиша",
      startsAt: "До старта {date}",
      cta: "Повачкаться",
    },
    tournamentBanner: {
      prizePool: "Обряк",
      endsIn: "До конца",
      startsIn: "До старта",
      ended: "Всё",
    },
    tournamentRewards: {
      sectionTitle: "Турики",
      endsIn: "ЗАКОНЧИЦЦА ЧЕРЕЗ",
      startsIn: "НАЧНЕЦЦА ЧЕРЕЗ",
      ended: "ВСЁ, РАСХОДИМСЯ",
      rules:
        "**Топ-50** лудоманов по профиту + **30 PvP номинаций** залутают **TON и NFT.** Обряк пухнет с каждой катки. Готов сорвать?",
      livePrizePool: "Обряк сач",
      prizePool: "Обряк",
      topPlayersPrefix: "Топ",
      topPlayersSuffix: "лудоманов",
      viewLeaderboard: "Глянуть топ",
      checkNominations: "Номинашки",
      startingSoon: "Турик вот-вот начнётся.",
      startsAt: "До старта {date}",
    },
    tournamentLeaderboard: {
      back: "Назад",
      prizePool: "обряк!",
      startsIn: "До старта",
      endsIn: "До конца",
      ended: "Всё",
      startsAt: "До старта {date}",
      rules:
        "Топ-50 по профиту и 30 PvP-номинашек залутают TON и NFT. Обряк пухнет с каждой катки.", //перевести
      empty: "Пока никого.",
      showMore: "Раскрыть",
      showLess: "Свернуть",
      rulesTitle: "Правила турика:",
      rule1:
        "Как считаются очки: Рейтинг = суммарный объём твоих выигрышей в TON.", //перевести
      rule2:
        "Раздача наград: По концу турика лудоманы получают награды по позиции в топе.", //перевести
      rule3:
        "Получение наград: Показанные награды выдадут победителям в течение суток после завершения и они появятся в хранилке MRKT.", //перевести
      rule4:
        "Только честный плей: Абуз, читы и любая подозрительная движуха = бан в турике без объяснений.", //перевести
      rule5:
        "Манипуляции и фарм: Лудоманы, катающие серии 10+ подряд 1v1 матчей против одного и того же оппа с 00:00 до 08:00 UTC с явным систематическим перекосом результатов, улетят в дисквал без предупреждения.", //перевести
      waiting: "Ждём лудомана",
    },
  },
} as const
